import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { container, gutters } from "@/components/ds";
import { formatAmountMg, formatUsd, priceTagParts } from "@/components/ds/priceTagFormat";
import { useDeals, useVendors, type DbListing } from "@/hooks/useDatabase";
import { useClipboardWithFeedback } from "@/hooks/useClipboardWithFeedback";
import { STORE_COUPON } from "@/lib/attribution";
import { isBhgLabs } from "@/lib/bhgLabs";
import { pubmedResultsLabel } from "@/lib/pubmedLabel";
import { cn } from "@/lib/utils";
import { HOME_PRICE_ROWS, shortStage, structureUrl, useHomePriceRows, type PriceRow } from "@/components/home/homeData";
import { HomeHeading, homeSectionY, Skeleton, SubHeading } from "@/components/home/parts";

/**
 * Homepage prices: the most viewed compounds with the lowest price per mg on file, then the vendor codes.
 *
 * Direction B (2026-10-24) puts the list on a white card with a soft shadow, but the list itself stays calm: it is
 * data, so hairline rows, foreground prices and no colour. The hero's "Lowest listed" card picks from these rows.
 *
 * Each row opens the compound page, where the full vendor table lives. No row carries a Buy button: a "from"
 * price next to a different vendor's Buy price read as bait and switch on the old cards.
 *
 * - The headline is the price per mg (lowestPriceListing, the same test as the compound page's "lowest price per
 *   mg" label), then the size and the pack price, then how many vendors list it.
 * - The dot plot (640px and up) shows every vendor's lowest price per mg for that compound on a line from $0 to the
 *   highest, so the spread between vendors shows at a glance. The key under the list says how to read it.
 * - Rows are ordered by page views across the whole table. Nothing says "live" or "updated daily": an editor
 *   approves prices after a scraper flags a change (claims sweep, 2026-09-14).
 * - From 1024px the most viewed compound gets a side panel with its PubChem structure (only a structure checked
 *   against PubChem, see homeData.ts), its research stage and its PubMed result count.
 *
 * Everything holds its height while the data loads: skeleton rows of the same size and a square image box.
 */
const ROWS = HOME_PRICE_ROWS;

/** "20 mg vial", "30 mg capsules": the size, and the form when it is not a vial. */
function sizeLabel(l: DbListing): string | null {
  if (!(l.total_mg > 0)) return null;
  const mg = formatAmountMg(l.total_mg);
  const form = (l.form_type || "").toLowerCase();
  if (form === "vial" || form === "blend" || form === "") return `${mg} vial`;
  if (form === "capsule") return `${mg} capsules`;
  if (form === "nasal") return `${mg} nasal spray`;
  return `${mg} ${form}`;
}

function sizeAndPrice(l: DbListing): string {
  const size = sizeLabel(l);
  return size ? `${size}, ${formatUsd(l.price)}` : formatUsd(l.price);
}

/** One dot per vendor on a line from $0 to the highest price per mg. The lowest is drawn solid. */
function VendorDots({ values, name, className }: { values: number[]; name: string; className?: string }) {
  if (values.length === 0) return <span aria-hidden className={cn("block h-3", className)} />;
  const max = values[values.length - 1];
  const label = `${name}: lowest price per mg at ${values.length} ${values.length === 1 ? "vendor" : "vendors"}, from ${formatUsd(values[0])} to ${formatUsd(max)}`;
  return (
    <span role="img" aria-label={label} className={cn("relative block h-3", className)}>
      <span aria-hidden className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
      {values.map((v, i) => (
        <span
          key={i}
          aria-hidden
          className={cn(
            "absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full",
            i === 0 ? "bg-foreground" : "border border-muted-foreground bg-background",
          )}
          // Inset by the dot's radius at both ends so the first and last dots stay on the line.
          style={{ left: `calc(5px + (100% - 10px) * ${max > 0 ? v / max : 0})` }}
        />
      ))}
    </span>
  );
}

// Phone: number, name over size, price over vendor count. From 640px: number, name over category, dot plot and
// vendor count, price over size, chevron.
const ROW_GRID =
  "grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-3 sm:grid-cols-[2.5rem_minmax(0,1fr)_9rem_10rem_1rem] sm:gap-x-5 lg:grid-cols-[2.5rem_minmax(0,1fr)_13rem_10.5rem_1rem] lg:gap-x-6";

function PriceRowItem({ row, rank }: { row: PriceRow; rank: number }) {
  const { compound, lowest, perVendor, vendorCount } = row;
  const headline = priceTagParts({ price: lowest.price, totalMg: lowest.total_mg }).headline;
  const vendors = `${vendorCount} ${vendorCount === 1 ? "vendor" : "vendors"}`;
  return (
    <li>
      <Link
        to={`/compound/${compound.slug}`}
        className={cn(
          ROW_GRID,
          "group min-h-16 px-4 py-2.5 transition-colors duration-150 hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-5",
        )}
      >
        <span className="type-small self-start pt-0.5 tabular-nums text-muted-foreground">{rank}</span>
        <span className="min-w-0">
          <span className="type-body block truncate font-semibold text-foreground group-hover:underline group-hover:underline-offset-4">
            {compound.name}
          </span>
          <span className="type-small block truncate tabular-nums text-muted-foreground sm:hidden">{sizeAndPrice(lowest)}</span>
          <span className="type-small hidden truncate text-muted-foreground sm:block">{compound.category}</span>
        </span>
        <span className="hidden min-w-0 items-center gap-3 sm:flex">
          <VendorDots values={perVendor} name={compound.name} className="min-w-0 flex-1" />
          <span className="type-small shrink-0 tabular-nums text-muted-foreground">{vendors}</span>
        </span>
        <span className="min-w-0 text-right">
          <span className="type-body block font-semibold tabular-nums text-foreground">{headline}</span>
          <span className="type-small block tabular-nums text-muted-foreground sm:hidden">{vendors}</span>
          <span className="type-small hidden truncate tabular-nums text-muted-foreground sm:block">{sizeAndPrice(lowest)}</span>
        </span>
        <ChevronRight aria-hidden className="hidden h-4 w-4 text-muted-foreground group-hover:text-foreground sm:block" />
      </Link>
    </li>
  );
}

function SkeletonRow() {
  return (
    <li aria-hidden className={cn(ROW_GRID, "min-h-16 px-4 py-2.5 sm:px-5")}>
      <Skeleton className="mt-1 h-3 w-3 self-start" />
      <span className="min-w-0">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-2 h-3 w-40 max-w-full" />
      </span>
      <Skeleton className="hidden h-3 w-full sm:block" />
      <span className="flex flex-col items-end">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="mt-2 h-3 w-14 sm:w-24" />
      </span>
      <span className="hidden sm:block" />
    </li>
  );
}

/** From 1024px: the most viewed compound, its structure and three facts. */
function LeadPanel({ row, loading }: { row: PriceRow | undefined; loading: boolean }) {
  const structure = row ? structureUrl(row.compound) : null;
  const pubmed = row?.compound.pubmed_count;
  const facts = [
    { label: "Stage", value: row ? shortStage(row.compound.research_stage) : null },
    { label: "Studies", value: typeof pubmed === "number" && pubmed > 0 ? pubmedResultsLabel(pubmed) : null },
    { label: "Lowest", value: row ? priceTagParts({ price: row.lowest.price, totalMg: row.lowest.total_mg }).headline : null },
  ];
  return (
    <div className="home-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <p className="type-small shrink-0 font-semibold text-muted-foreground">Most viewed</p>
        {/* Fills the rest of the row, so the name arriving changes the text, not the box. */}
        <p className="type-small min-w-0 flex-1 truncate text-right font-semibold text-foreground">{row ? row.compound.name : "\u00a0"}</p>
      </div>
      {/* PubChem draws its structures on white, so the plate is white in both themes (the one place this page uses
          a fixed colour). The box is square from the first frame: the image landing moves nothing. */}
      <figure className="border-b border-border">
        <div className={cn("flex aspect-square items-center justify-center p-4", structure ? "bg-white" : "bg-secondary")}>
          {structure ? (
            <img
              src={structure}
              alt={`2D structure of ${row?.compound.name}`}
              width={300}
              height={300}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-contain"
            />
          ) : loading ? (
            <Skeleton className="h-2/3 w-2/3" />
          ) : (
            <span className="type-small px-4 text-center text-muted-foreground">No structure on file for this compound.</span>
          )}
        </div>
        <figcaption className="type-small px-4 py-2 text-muted-foreground">{structure ? "2D structure from PubChem" : "\u00a0"}</figcaption>
      </figure>
      <dl className="divide-y divide-border px-4">
        {facts.map((f) => (
          <div key={f.label} className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-baseline gap-3 py-2.5">
            <dt className="type-small text-muted-foreground">{f.label}</dt>
            <dd className="type-small min-w-0 truncate font-medium tabular-nums text-foreground">
              {loading ? <Skeleton className="h-3.5 w-20" /> : f.value ?? <span className="font-normal text-muted-foreground">Not listed</span>}
            </dd>
          </div>
        ))}
      </dl>
      <Link
        to={row ? `/compound/${row.compound.slug}` : "/compare"}
        className="type-small flex min-h-11 items-center justify-between gap-2 border-t border-border px-4 font-medium text-primary underline-offset-4 hover:underline"
      >
        <span className="min-w-0 truncate">{row ? `Read the ${row.compound.name} guide` : "Compare all prices"}</span>
        <ArrowRight aria-hidden className="h-4 w-4 shrink-0" />
      </Link>
    </div>
  );
}

function CodeCell({ vendorName, code, note }: { vendorName: string; code: string; note?: string }) {
  const { copy, copied } = useClipboardWithFeedback({
    text: code,
    onSuccess: () => toast.success(`Copied ${code} for ${vendorName}`),
  });
  return (
    <button
      type="button"
      onClick={() => copy()}
      title={note ? `${vendorName}: ${note}` : vendorName}
      aria-label={`Copy code ${code} for ${vendorName}${note ? `, ${note}` : ""}`}
      className="home-card home-card-link group flex h-full min-h-14 w-full flex-col justify-center px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-4"
    >
      <span className="type-small block w-full truncate text-muted-foreground">{vendorName}</span>
      <span className="flex w-full items-center justify-between gap-2">
        <span className="type-body min-w-0 truncate font-mono font-semibold text-foreground">{code}</span>
        {copied ? (
          <Check aria-hidden className="h-4 w-4 shrink-0 text-success" />
        ) : (
          <Copy aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
        )}
      </span>
    </button>
  );
}

function VendorCodes() {
  const { data: vendors = [] } = useVendors();
  const { data: allDeals = [] } = useDeals();

  const codes = useMemo(() => {
    const priorityOf = new Map(vendors.map((v) => [v.id, v.priority ?? 999]));
    // Same vendor order as the rest of the site (vendors.priority), so the list does not reshuffle every time a
    // deal row is edited.
    const fromDeals = allDeals
      .filter((d) => d.active && d.coupon_code && (!d.vendor_id || priorityOf.has(d.vendor_id)))
      .sort((a, b) => (priorityOf.get(a.vendor_id ?? "") ?? 999) - (priorityOf.get(b.vendor_id ?? "") ?? 999))
      .map((d) => ({ vendorName: d.vendor_name, code: d.coupon_code as string, note: d.discount || undefined }));
    const bhg = vendors.find((v) => isBhgLabs(v));
    const list = bhg ? [{ vendorName: bhg.name, code: STORE_COUPON, note: "20% off your first order" }, ...fromDeals] : fromDeals;
    // One code per vendor.
    const seen = new Set<string>();
    return list.filter((c) => (seen.has(c.vendorName) ? false : (seen.add(c.vendorName), true))).slice(0, 6);
  }, [allDeals, vendors]);

  if (codes.length === 0) return null;
  return (
    <div className="mt-8">
      <SubHeading
        action={
          <Link
            to="/deals"
            className="type-small -my-2.5 inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
          >
            All deals <ArrowRight aria-hidden className="h-4 w-4" />
          </Link>
        }
      >
        Vendor codes
      </SubHeading>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {codes.map((c) => (
          <li key={c.vendorName} className="min-w-0">
            <CodeCell vendorName={c.vendorName} code={c.code} note={c.note} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HomePrices() {
  const { rows, loading } = useHomePriceRows(ROWS);

  return (
    <section aria-labelledby="home-prices-title" className={cn(container.default, gutters, homeSectionY)}>
      <HomeHeading
        id="home-prices-title"
        title="Lowest price per mg"
        summary="The six most viewed compounds. Prices change, so check the vendor before you buy."
        action={{ label: "Compare all prices", href: "/compare" }}
      />
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_16rem] lg:items-start lg:gap-x-10">
        <div className="min-w-0">
          {!loading && rows.length === 0 ? (
            <p className="home-card type-body px-5 py-6 text-muted-foreground">
              Prices did not load.{" "}
              <Link to="/compare" className="font-medium text-primary underline-offset-4 hover:underline">
                See every price on the compare page
              </Link>
              .
            </p>
          ) : (
            <ol className="home-card divide-y divide-border overflow-hidden" aria-busy={loading || undefined}>
              {loading
                ? Array.from({ length: ROWS }).map((_, i) => <SkeletonRow key={i} />)
                : rows.map((row, i) => <PriceRowItem key={row.compound.id} row={row} rank={i + 1} />)}
            </ol>
          )}
          <p className="type-small mt-3 hidden text-muted-foreground sm:block">
            Each dot is one vendor&apos;s lowest price per mg, on a line from $0 to that compound&apos;s highest. The solid dot is the lowest.
          </p>
          <VendorCodes />
        </div>
        <aside aria-label="The most viewed compound" className="hidden lg:block">
          <LeadPanel row={rows[0]} loading={loading} />
        </aside>
      </div>
    </section>
  );
}
